export const FRAMEWORK_TEMPLATE_CONTRACT_VERSION = 1;
export const DOCUMENT_EVIDENCE_TEMPLATE_CAPABILITY_VERSION = 1;
export const FRAMEWORK_TEMPLATE_NODE_TYPES = ["richSection"];
export const FRAMEWORK_ENTITY_MODEL_CONTRACT_VERSION = 1;
export const FRAMEWORK_ENTITY_VALUE_TYPES = [
    "text",
    "richTextBasic",
    "enum",
    "multiEnum",
    "number",
    "date",
    "boolean",
    "user",
];
export const FRAMEWORK_ENTITY_DEFINITION_ROLES = ["attribute", "owner"];
export const FRAMEWORK_ENTITY_CAPABILITY_KEYS = [
    "versioned",
    "clarifiable",
    "mentionable",
];
export const ENTITY_CAPABILITIES = {
    versioned: true,
    clarifiable: true,
    mentionable: true,
};
export const FRAMEWORK_COLOR_TOKENS = [
    "neutral",
    "info",
    "success",
    "warning",
    "danger",
    "tier-1",
    "tier-2",
    "tier-3",
    "tier-4",
];
/**
 * THE sort contract for catalog attributes: `listOrder` ascending, ties broken by `attributeId`.
 *
 * Order is load-bearing, not cosmetic. The first owner attribute of an entity type is the one a
 * clarification is routed to (`clarificationTriage`) and the one treated as the entity's
 * effective owner (`projectEntities`), while the same list drives what the UI shows first. Two
 * attributes may legitimately share a `listOrder`, so the tiebreak is what keeps every surface
 * naming the same owner. `attributeId` is the stable persisted identity of an attribute, so the
 * order is deterministic across releases and independent of catalog insertion order.
 *
 * The tiebreak compares code units rather than calling `localeCompare`, which orders by the
 * runtime's locale: a browser in a locale that sorts case or accents differently from the Convex
 * runtime would put a different owner first, which is the exact cross-surface disagreement this
 * contract exists to remove. Nothing constrains `attributeId` to a character set — every id the
 * shipped frameworks use is ASCII camelCase, but the schema stores a free-form string — so the
 * comparator must not depend on the locale it happens to run in.
 *
 * Every consumer sorts through this comparator — never re-sort catalog attributes by hand.
 */
export function compareCatalogAttributes(left, right) {
    if (left.listOrder !== right.listOrder) {
        return left.listOrder - right.listOrder;
    }
    if (left.attributeId === right.attributeId) {
        return 0;
    }
    return left.attributeId < right.attributeId ? -1 : 1;
}
/** An attribute a framework author has not switched off. */
export function isActiveCatalogAttribute(attribute) {
    return attribute.disabledAt == null;
}
/**
 * The active attributes of one entity type in one role, in the one sort contract.
 *
 * Accepts any iterable of attribute rows — an array from a compiled catalog or the values of the
 * catalog index's per-entity-type map — so a caller never has to reimplement the filter.
 */
export function selectCatalogAttributes(attributes, entityTypeKey, role) {
    return [...(attributes ?? [])]
        .filter((attribute) => attribute.entityTypeKey === entityTypeKey &&
        attribute.role === role &&
        isActiveCatalogAttribute(attribute))
        .sort(compareCatalogAttributes);
}
/** The active owner attributes of an entity type, in the one sort contract. */
export function selectCatalogOwnerAttributes(attributes, entityTypeKey) {
    return selectCatalogAttributes(attributes, entityTypeKey, "owner");
}
/**
 * The active metadata attributes an entity type offers as filters in one context, in the one sort
 * contract — the filter-field twin of {@link selectCatalogOwnerAttributes}.
 */
export function selectCatalogMetadataFilterAttributes(attributes, entityTypeKey, context) {
    return selectCatalogAttributes(attributes, entityTypeKey, "attribute").filter((attribute) => attribute.filterContexts?.includes(context) === true);
}
